'use client'

import { partnerWaitlistAction } from '@/lib/actions'
import { formSchema } from '@/lib/partner-waitlist'
import { BlokProps } from '@/lib/types'
import { PartnerWaitlistStoryblok } from '@/lib/types/component-types-sb'
import { zodResolver } from '@hookform/resolvers/zod'
import { storyblokEditable } from '@storyblok/react'
import { useActionState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Container } from '../shared/container'
import { DynamicForm } from '../shared/dynamic-form'
import { RichText } from '../shared/rich-text'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'

function PartnerWaitlistForm() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      projectSize: 'LT_50K' as const,
      acceptTerms: false,
    },
  })

  const [state, formAction, isPending] = useActionState(partnerWaitlistAction, {
    success: false,
    message: '',
  })

  useEffect(() => {
    if (state.success && state.message !== '') {
      form.reset()
      toast.success(state.message)
    }
  }, [state.success, state.message, form])

  return (
    <div className="space-y-4">
      {!state.success && state.message !== '' && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {state.message}
            {state.errors && (
              <ul className="list-disc">
                {Object.keys(state.errors).map((key) => (
                  <li key={key}>{state.errors[key]}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}
      <DynamicForm
        action={formAction}
        isPending={isPending}
        form={form}
        fields={[
          {
            name: 'companyName',
            label: 'Company Name',
            type: 'text',
          },
          {
            fields: [
              {
                name: 'firstName',
                label: 'First Name',
                type: 'text',
              },
              {
                name: 'lastName',
                label: 'Last Name',
                type: 'text',
              },
            ],
          },
          {
            name: 'email',
            label: 'Email',
            type: 'email',
          },
          {
            name: 'projectSize',
            label: 'Typical Project Size (USD)',
            type: 'select',
            options: [
              { label: 'Less than $50,000', value: 'LT_50K' },
              { label: '$50,000 - $250,000', value: 'OPT50K_250K' },
              { label: '$250,000 - $500,000', value: 'OPT250K_500K' },
              { label: 'Greater than $500,000', value: 'GT_500K' },
            ],
          },
          {
            name: 'acceptTerms',
            label: 'I agree to the privacy policy and terms of service',
            type: 'checkbox',
          },
        ]}
        submitButton={{ label: 'Join Waitlist' }}
      />
    </div>
  )
}

export function PartnerWaitlistFormSection({ blok }: BlokProps<PartnerWaitlistStoryblok>) {
  return (
    <section
      {...storyblokEditable(blok)}
      className="border-border border-y py-24"
    >
      <Container>
        <div className="mx-auto space-y-8 lg:w-[600px]">
          <RichText content={blok.content} />
          <PartnerWaitlistForm />
        </div>
      </Container>
    </section>
  )
}
